import numOrDefault from 'lib/helpers/numOrDefault';
import { ia, iaRa, inRn } from 'lib/helpers/utility';

import { amountToCents } from '../../Claims/CMS1500/lib/amountToCents';
import { denied_codes, patient_responsibility } from '../../lib/insuranceAdjustmentGroups';

import { calcLatestBenefits } from './calcLatestBenefits';
import { renderType } from './renderType';

export const formatProcedureAdjustments = ({ procDetails, adjustments }) => {
  const adjustmentsLength = adjustments?.length - 1 || 0;

  if (procDetails?.denied) {
    return {
      ...procDetails,
      ins_adjustment: 0,
      ins_payment: 0,
      ptBalance: numOrDefault(procDetails?.charge),
      ins_balance: 0
    };
  }

  let insAdj = 0;
  let insBalance = 0;
  adjustments[adjustmentsLength]?.ins_adj?.forEach((item) => {
    const { group, code, amount, type = '' } = item;

    const hasPRCode = patient_responsibility[group]?.includes(+code) || type == 'pt_rep';
    const hasDeniedCode = denied_codes[group]?.includes(code);

    if (type == 'ins_balance') {
      insBalance += amountToCents(amount);
    }

    if ((group !== 'PR' && !hasPRCode && !hasDeniedCode) || type == 'adj') {
      insAdj += amountToCents(amount);
    }
  });

  let ptAdj = 0;
  adjustments[adjustmentsLength]?.ins_adj?.forEach((item) => {
    const { group, code, amount, type = '' } = item;

    const hasPRCode = patient_responsibility[group]?.includes(+code) || type == 'pt_rep';
    const hasDeniedCode = denied_codes[group]?.includes(code);

    if (group === 'PR' || !!hasPRCode || !!hasDeniedCode) {
      ptAdj += amountToCents(amount);
    }
  });

  if (ia(adjustments[adjustmentsLength]?.pt_adj)) {
    adjustments[adjustmentsLength]?.pt_adj?.forEach((item) => {
      const { group, code, amount, type = '' } = item;

      const hasPRCode = patient_responsibility[group]?.includes(+code) || type == 'pt_rep';
      const hasDeniedCode = denied_codes[group]?.includes(code);

      if (group === 'PR' || !!hasPRCode || !!hasDeniedCode) {
        ptAdj += amountToCents(amount);
      }
    });
  }

  const ins_balance =
    numOrDefault(procDetails?.charge) -
    numOrDefault(insAdj) -
    numOrDefault(adjustments[adjustmentsLength]?.paid) -
    numOrDefault(procDetails?.other_adj) -
    numOrDefault(ptAdj) +
    numOrDefault(insBalance);

  const childrenItemRow = {
    ...procDetails,
    ins_adjustment: insAdj,
    ins_payment: adjustments[adjustmentsLength]?.paid,
    ptBalance: ptAdj,
    ins_balance
  };

  return childrenItemRow;
};

export const formatInvoiceProcedures = ({ invoices = [] }) => {
  const rows = [];

  iaRa(invoices).forEach((item) => {
    const isPackageOrProduct = ['package', 'product'].includes(item?.type?.toLowerCase());

    const calculatedInsBalance =
      numOrDefault(item?.charge) -
      numOrDefault(item?.ins_adjustment) -
      numOrDefault(item?.ins_payment) -
      numOrDefault(item?.other_adj) -
      numOrDefault(item?.pt_balance);

    const ins_balance = isPackageOrProduct
      ? 0
      : item?.isEstimated
        ? item?.charge - numOrDefault(item?.other_adj)
        : calculatedInsBalance;

    const qty = (inRn(item?.quantity) ?? inRn(item?.units) ?? inRn(item?.sales_count)) || 1;

    const ptBalance =
      (item?.ptBalance
        ? item?.ptBalance
        : ia(item?.primary)
          ? calcLatestBenefits({ procedure: item })?.ptBalance
          : 0) *
      (!item?.type || ['product', 'package'].includes(item?.type?.toLowerCase()) ? 1 : qty);

    const itemRow = {
      ...item,
      details: item?.details,
      dateofservice: item?.dateofservice,
      type: item?.primary ? renderType(item) : item?.type,
      qty,
      charge: item?.charge,
      ins_payment: item?.ins_payment,
      ptBalance,
      ins_adjustment: item?.isEstimated
        ? item?.ins_adjustment
        : !ia(item?.primary)
          ? item?.ins_adjustment
          : (calcLatestBenefits({ procedure: item })?.insAdj ?? item?.ins_adjustment ?? 0),
      ins_balance,
      show: item?.show ?? true
    };

    const children = [];
    if (ia(item?.primary)) {
      children.push(
        formatProcedureAdjustments({
          procDetails: {
            details: item?.details,
            dateofservice: item?.dateofservice,
            type: 'Primary',
            qty,
            charge: item?.charge,
            denied: item?.denied
          },
          adjustments: item?.primary
        })
      );
    }

    if (ia(item?.secondary)) {
      children.push(
        formatProcedureAdjustments({
          procDetails: {
            details: item?.details,
            dateofservice: item?.dateofservice,
            type: 'Secondary',
            qty,
            charge: item?.charge
          },
          adjustments: item?.secondary,
          denied: item?.denied
        })
      );
    }

    if (ia(item?.tertiary)) {
      children.push(
        formatProcedureAdjustments({
          procDetails: {
            details: item?.details,
            dateofservice: item?.dateofservice,
            type: 'Tertiary',
            qty,
            charge: item?.charge
          },
          adjustments: item?.tertiary,
          denied: item?.denied
        })
      );
    }

    if (ia(children)) {
      itemRow.children = children;
    }

    rows.push(itemRow);
  });
  return rows;
};
